import type { KeyboardEvent } from 'react';

/**
 * Run a click handler from the keyboard.
 *
 * Some rows in the app are clickable containers rather than buttons — a table row
 * that opens its detail, a log line that opens its drawer. They cannot become a
 * `<button>` without breaking table or layout semantics, so they carry
 * `tabIndex={0}` plus this handler, which is what makes them reachable and
 * operable without a pointer.
 *
 * Enter and Space are the two keys a native button responds to. Space is
 * prevented from its default scroll so activating a focused row does not also
 * jump the page.
 */
export function activateOnKey(e: KeyboardEvent, run: () => void): void {
  if (e.key !== 'Enter' && e.key !== ' ') return;
  e.preventDefault();
  run();
}

/**
 * True when the event came from inside a row's action cluster.
 *
 * Clickable rows nest real buttons (edit, pause, delete). Those must not also
 * trigger the row's own navigation, and checking the origin here means the action
 * cluster does not need an interactive handler of its own just to stop the bubble.
 */
export function isFromRowActions(e: { target: unknown }): boolean {
  return e.target instanceof Element && e.target.closest('.row-actions') !== null;
}
